// src/components/Settings/ProfileCard.tsx
import React, { useState, useEffect } from 'react';
import DefaultUser from '../../assets/user-default.jpg';
import { changeName } from '../../api/userService';

interface UserData {
  email: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  born_date?: string;
  language_code?: string;
}

interface ProfileCardProps {
  userData: UserData | null;
  refreshUserData: () => Promise<void>;
  t: Function;
  setShowNotification: React.Dispatch<React.SetStateAction<boolean>>;
  setNotificationMessage: React.Dispatch<React.SetStateAction<string>>;
  handleLogout: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  userData,
  refreshUserData,
  t,
  setShowNotification,
  setNotificationMessage,
  handleLogout
}) => {
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');

  useEffect(() => {
    if (userData) {
      setNewFirstName(userData.first_name || '');
      setNewLastName(userData.last_name || '');
    }
  }, [userData]);

  const handleChangeName = async () => {
    try {
      await changeName(newFirstName, newLastName);
      await refreshUserData();

      setNotificationMessage(t('settings.notifications.nameUpdated'));
      setShowNotification(true);
    } catch (err) {
      console.error(err);
    }
  };

  if (!userData) return null;

  const createdAtDate = new Date(userData.created_at);
  const formattedCreatedAt = createdAtDate.toLocaleDateString('es-ES');

  return (
    <div className="bg-[#0f172a] rounded-lg shadow-md p-6 flex flex-col items-center">
      {/* Foto */}
      <div className="w-32 h-32 mb-4">
        <img
          src={DefaultUser}
          alt="User Avatar"
          className="object-cover w-full h-full rounded-full"
        />
      </div>

      {/* Info del usuario */}
      <div className="text-center w-full text-white">
        <h2 className="text-xl font-semibold mb-1">
          {userData.first_name} {userData.last_name}
        </h2>
        <p className="text-sm opacity-80">{userData.email}</p>
        <p className="text-sm mt-2">
          <strong>{t('settings.labels.createdAt')}:</strong> {formattedCreatedAt}
        </p>

        {/* Botón Logout (en rojo) */}
        <button
          onClick={handleLogout}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 mt-4 rounded"
        >
          {t('settings.buttons.logout')}
        </button>

        {/* Cambiar nombre */}
        <div className="mt-6">
          <h3 className="font-semibold mb-2">{t('settings.changeNameTitle')}</h3>
          <div className="flex flex-col gap-2 md:flex-row md:gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium block mb-1">
                {t('settings.labels.newFirstName')}:
              </label>
              <input
                className="
                  bg-gray-700 text-white 
                  border border-gray-600 
                  p-2 
                  rounded 
                  w-full
                "
                type="text"
                value={newFirstName}
                onChange={(e) => setNewFirstName(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium block mb-1">
                {t('settings.labels.newLastName')}:
              </label>
              <input
                className="
                  bg-gray-700 text-white 
                  border border-gray-600 
                  p-2 
                  rounded
                  w-full
                "
                type="text"
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
              />
            </div>
          </div>
          <button
            className="
              bg-gray-800 
              text-white 
              border border-gray-600 
              rounded 
              px-4 py-2 
              mt-4 
              w-full md:w-auto
              hover:bg-gray-700
            "
            onClick={handleChangeName}
          >
            {t('settings.buttons.saveName')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
